/**
 * The contract every voice button consumes. One interface, five widgets, two hosts.
 *
 * WHY THIS EXISTS RATHER THAN EACH BUTTON TAKING A `CallSession`. Two reasons, and
 * the second is the one that forced it.
 *
 * 1. `CallSession` is a big object with a diagnostic surface — RTP stats, recorder
 *    counters, clock offsets, provenance. A button needs about eight facts. Handing
 *    every widget the whole thing invites one of them to reach for `counters` and
 *    quietly become untestable outside a real call.
 * 2. THESE COMPONENTS RUN IN TWO PLACES. In the panel they are driven by a real
 *    `CallSession`; in the playground's preview they are ALSO driven by a real one;
 *    but a Storybook-style state preview, and any test, needs to render "listening"
 *    or "error" without a microphone, a peer connection or a server. A narrow
 *    interface makes a fake trivially constructible. A `CallSession` does not.
 *
 * THE ADAPTER IS `useVoiceControl` in `use-voice-control.ts`. Nothing here imports
 * `CallSession`, on purpose: this file is the boundary, and if a button ever needs
 * something the interface does not carry, the right move is to widen the interface
 * deliberately rather than to reach past it.
 *
 * ONE FIELD IS A FUNCTION AND IT IS NOT AN OVERSIGHT. `levels()` is read inside a
 * `requestAnimationFrame` loop by whichever component draws. Everything else here
 * changes at human speed — a few times a second at most — and drives React
 * normally. Audio amplitude changes sixty times a second and must not. See the
 * header of `lib/call/meter.ts`.
 */

/**
 * The phases a button has to render, which is deliberately FEWER than
 * `CallPhase` carries.
 *
 * `CallPhase` distinguishes `checking` (probing browser support) from `starting`
 * (creating the session, opening the mic) because a diagnostics page wants to say
 * which one failed. A button cannot usefully say either — both are "wait a moment"
 * — so they collapse into `connecting`. Likewise `blocked` and `failed` are both
 * `error` here; the message says which.
 *
 * THERE IS NO `ending`, AND THE STAGE IT NAMES STILL EXISTS (2026-08-24). Hanging
 * up is a real piece of work — drain the recorder, close the peer connection, tell
 * the server twice — and `CallPhase` still reports every step of it, which is what
 * the diagnostics page shows. A WIDGET renders none of it. The drain normally takes
 * a few hundred milliseconds, so a state of its own bought one thing: a spinner or
 * an "Ending…" label that appeared and vanished, which reads as a flicker rather
 * than as progress. Pressing the end button now shows the call ended immediately —
 * optimistic in the strict sense (the teardown is still running) and honest in the
 * one that matters, because the conversation IS over the moment the button is
 * pressed and nothing the visitor can do changes what happens next.
 *
 * The one thing that optimism costs is sequencing, and it is paid in the adapter: a
 * visitor who sees "ended" may press start again while the old call is still
 * draining, so `useVoiceControl` queues that start behind the teardown rather than
 * letting the two interleave. See its `start`.
 *
 * `thinking` is the one phase that has NO equivalent in `CallPhase`, and it is the
 * most valuable to a visualiser: the stretch after the user stops speaking and
 * before the agent starts. It is derived, not reported — see the adapter.
 */
export type VoicePhase =
  | 'idle'
  | 'connecting'
  | 'listening'
  | 'thinking'
  | 'speaking'
  | 'ended'
  | 'error'

/** One line of conversation, for the widgets that show any. */
export interface VoiceTranscriptLine {
  id: string
  who: 'user' | 'assistant'
  text: string
  /** False while the provider is still streaming this item. */
  final: boolean
}

/**
 * One tool the model reached for, for the widgets that show a conversation.
 *
 * A NARROWED `ToolActivity`, and the narrowing is the same discipline the rest of
 * this file applies: the call layer's version carries `startedAtMs`/`endedAtMs` as
 * two epoch stamps, and a widget only ever wants the duration, so it gets the
 * duration. What is NOT here is arguments and results, and their absence is a
 * security property rather than a simplification — they never ride the control
 * channel at all, because a public embed visitor holds the same channel. See the
 * header of `components/call/transcript.tsx`.
 *
 * `anchorIndex` is how many transcript lines existed when the call started, which
 * is what lets a widget interleave the card where the conversation actually stood.
 * Lines carry no timestamps here, so position is the only ordering available.
 */
export interface VoiceToolCall {
  id: string
  /** The raw name, `get_weather`. A widget titles it for display and keeps the
   *  raw one visible, because the raw one is what the session record uses. */
  name: string
  /** Operator-written display label ("Order lookup") and short blurb, set on
   *  the tool definition for exactly this surface and carried on the wire's
   *  `tool` message. Null when unset — widgets fall back to the mono name. */
  displayName: string | null
  displayDescription: string | null
  state: 'running' | 'ok' | 'error'
  /** Seconds, or null while it is still running. */
  seconds: number | null
  anchorIndex: number
}

/**
 * One screen card from the built-in `show_info` tool, for the widgets that show
 * a conversation.
 *
 * A NARROWED `CardActivity`, the same discipline as `VoiceToolCall` above: the
 * call layer's `atMs` is display furniture no widget wants, so it is not here.
 * What IS here is the whole of what the model composed — an optional title and
 * a markdown-subset body — because unlike a tool call this content is MEANT for
 * the visitor's eyes: it is the same trust class as the words the model speaks,
 * not arguments or results (those still never ride the control channel at all).
 * The body must only ever be rendered through `lib/mini-markdown.tsx`, the
 * whitelist renderer — never as HTML.
 *
 * `anchorIndex` is the same ordering `VoiceToolCall` carries: how many
 * transcript lines existed when the card arrived.
 */
export interface VoiceCard {
  id: string
  /** Optional heading. Null when the model sent none. */
  title: string | null
  /** Markdown subset: paragraphs, bold, italic, lists, https links. */
  body: string
  anchorIndex: number
}

/** Mirror of lib/call's StartStage, declared structurally here because this
 *  file deliberately imports nothing (see the header). The adapter's typecheck
 *  is what holds the two unions together. */
export type VoiceStartStage = 'checking' | 'security' | 'audio' | 'joining'

export interface VoiceControl {
  phase: VoicePhase
  /**
   * Which leg of the start sequence a `connecting` phase is on, so a widget's
   * "Connecting…" line can narrate instead of blinking dots at the caller for
   * four seconds. Optional and nullable: hand-made controls and older widgets
   * omit it, and a widget without somewhere to put words ignores it.
   */
  stage?: VoiceStartStage | null
  /**
   * A person in the panel has taken the call over from the AI. Optional, like
   * `stage`: hand-made controls omit it. The widgets that show a status line
   * say so (`withTakeover` in stage-text.ts); nothing else about them changes.
   */
  takenOver?: boolean
  /** Human-readable, already written for an end user rather than an operator.
   *  Null unless `phase` is `error`. */
  error: string | null
  /** Non-fatal things worth surfacing. A widget may ignore these; the panel does
   *  not. Never an error — the call is working. */
  notices: readonly string[]
  muted: boolean
  /** Epoch millis, or null before the call connected. A widget that shows a timer
   *  ticks its own interval off this rather than receiving a changing prop. */
  startedAtMs: number | null
  transcript: readonly VoiceTranscriptLine[]
  /**
   * Tool calls, interleaved into the transcript by `anchorIndex`.
   *
   * ADDED 2026-08-25 FOR THE CHAT FLAG, which is the first widget with somewhere to
   * put them — and added to the INTERFACE rather than reached for past it, which is
   * the move this file's header asks for when a widget needs something the contract
   * does not carry. The other widgets ignore it; a hand-made control in a test
   * supplies an empty array.
   */
  tools: readonly VoiceToolCall[]
  /**
   * Screen cards from the `show_info` tool, interleaved by `anchorIndex` the
   * same way `tools` is.
   *
   * ADDED 2026-08-26, under the same rule as `tools`: widened deliberately
   * because the chat flag has somewhere to paint a card. The widgets without a
   * chat panel ignore it — an orb has no screen to put a card on, and the tool's
   * executor already told the model so. A hand-made control in a test supplies
   * an empty array.
   */
  cards: readonly VoiceCard[]

  /**
   * Live loudness per direction, 0..1. CALL THIS INSIDE YOUR OWN ANIMATION FRAME.
   *
   * May legitimately return zeroes forever — there is no AudioContext on the
   * fallback recorder path — so anything that draws must stay alive on `phase`
   * alone when it does. Every widget here does.
   */
  levels: () => { user: number; assistant: number }

  /**
   * Frequency bins for one direction, 0..255, written into the caller's buffer.
   * Returns how many were written, or 0 when there is nothing to read.
   *
   * OPTIONAL, and the optionality is the point. The spectrum-driven widgets need it
   * — the ported ElevenLabs bar and Bars — while a hand-made `VoiceControl` in a
   * test or a state preview should not have to fake an FFT to render a button. A
   * consumer must therefore handle its absence; both visualizers fall back to a
   * level-driven form.
   *
   * Same rule as `levels`: called inside an animation frame, never a prop.
   */
  spectrum?: (track: 'user' | 'assistant', out: Uint8Array) => number

  /** Idempotent. Calling `start` while already live does nothing, which matters
   *  because a button can be double-clicked and a WebRTC offer is not idempotent. */
  start: () => void
  stop: () => void
  toggleMute: () => void
  /** Type into the conversation. Only the widgets with a text input use it. */
  sendText: (text: string) => void
}

/**
 * True when the call is doing something the user should not interrupt.
 *
 * The comment here used to say "used for the `beforeunload` guard in the embed".
 * There is no such guard, in the embed or anywhere else — the sentence described an
 * intention, and a comment that describes code which does not exist is worse than no
 * comment because it stops the next person looking. Its only caller is the
 * playground, which uses it to refuse to move a live call between cards.
 *
 * A `beforeunload` guard would be a reasonable thing to add: an embedded widget whose
 * visitor closes the tab mid-sentence currently ends the call through the `keepalive`
 * request in the embed backend, which is correct but silent. That is a product
 * decision about interrupting somebody's navigation, not a gap, so it is written down
 * rather than assumed.
 */
export function isBusy(phase: VoicePhase): boolean {
  return phase === 'connecting' || phase === 'listening' || phase === 'thinking' || phase === 'speaking'
}

/** True when the microphone is open and the conversation is running. Distinct from
 *  `isBusy`, which includes the connecting stretch where it is not yet. */
export function isLive(phase: VoicePhase): boolean {
  return phase === 'listening' || phase === 'thinking' || phase === 'speaking'
}

// ============================================================================
// Shared configuration
// ============================================================================

/**
 * The options every widget shares, so the playground can render one config editor
 * for the common half and only a small per-variant panel for the rest.
 *
 * `label` and `endLabel` are capped by the widgets that have narrow room for them
 * (the flag's rotated text is the tightest), and the cap is enforced in the editor
 * rather than here — a truncated string silently is worse than a form that says
 * "22 characters maximum".
 */
export interface VoiceWidgetCommon {
  /** Shown before the call starts — or, on the three widgets whose control is an
   *  icon (bar, bars, orb), the tooltip and screen-reader name instead. */
  label?: string
  /**
   * Shown while live, on the control that ends the call — and only two of the five
   * have somewhere to show it: the flag paints it in the expanded pill and the plain
   * button paints it as its own text. On the conversation bar, Bars and the orb the
   * control is an icon, so this reaches an `aria-label` (and a tooltip on the bar)
   * and nothing else; from 2026-08-25 the config editor does not offer it for those
   * three, and their component defaults stand unless a hand-written tag sets
   * `data-end-label`.
   */
  endLabel?: string
  /** The accent colour. A CSS colour, used for the primary surface. */
  accent?: string
  /** Text/icon colour ON the accent. Separate because a yellow flag needs black
   *  text and a navy one needs white, and guessing from luminance gets the
   *  in-between cases wrong. */
  onAccent?: string
  /** A small caption. The reference implementation calls this "powered by". */
  caption?: string
  /**
   * Follow the host page's colour scheme where the widget supports it — which is the
   * two CLASS-BASED widgets, the orb and the plain button. It works by putting
   * `vai-dark`/`vai-light` on a wrapper inside the shadow root for `embed.css`'s
   * tokens to key off, so the three widgets that carry no class names cannot see it
   * at all. The editor offers it to the two that can (see `readsTheme`).
   */
  theme?: 'light' | 'dark' | 'auto'
}

/** Where a floating widget sits. The flag only uses the two side values; the
 *  others only use the four corners. Kept as one union so the embed's attribute
 *  parsing has a single validator. */
export type VoicePlacement =
  | 'left'
  | 'right'
  | 'bottom-right'
  | 'bottom-left'
  | 'top-right'
  | 'top-left'
  | 'inline'
